<template>
  <BaseDialog max-width="900" persistent :value="true">
    <template #modal>
      <BaseCosmoCard>
        <template #title>
          <div class="modal_header">
            <BaseTitle v-text="dialogTitle" />
            <BaseButtonIcon
              class="close_icon"
              icon="$mdiClose"
              :disabled="isSubmiting"
              @click="reset()"
            />
          </div>
        </template>
        <div class="modal_content">
          <v-progress-linear
            v-if="isSubmiting"
            class="progression"
            indeterminate
          />

          <!-- ACTION SECTION -->
          <div class="input_group">
            <div class="input_section">
              <!-- ACTION SET_STATUS  -->
              <template v-if="isInitializing">
                <v-skeleton-loader class="notification_skeleton" type="text" />
              </template>
              <template v-else>
                <div
                  v-if="effect.action === AUTOMATION_EFFECT.SET_STATUS"
                  class="action_section"
                >
                  <div class="input_label" v-text="$t('utils.destination')" />
                  <base-autocomplete
                    v-model="effect.content_id"
                    :items="workflowStatuses"
                    :placeholder="$t('automation.dialog.choose_target_status')"
                    outlined
                    item-text="name"
                    item-value="id"
                    :disabled="isSubmiting"
                    :error-messages="effectContentError"
                    :has-error="!!effectContentError"
                    clearable
                  />
                </div>
                <!-- ACTION SEND_OFFER  -->
                <div
                  v-else-if="effect.action === AUTOMATION_EFFECT.SEND_OFFER"
                  class="action_section"
                >
                  <div class="input_label" v-text="$t('utils.template')" />
                  <base-autocomplete
                    v-model="effect.content_id"
                    :items="offerTemplates.items"
                    outlined
                    item-text="title"
                    item-value="id"
                    :disabled="isSubmiting"
                    :error-messages="effectContentError"
                    :has-error="!!effectContentError"
                    :loading="fetchingOfferTemplate"
                    clearable
                  />
                </div>
                <div
                  v-else-if="effect.action === AUTOMATION_EFFECT.SEND_FORM"
                  class="action_section"
                >
                  <div class="input_label" v-text="$t('utils.template')" />
                  <!-- ACTION SEND_FORM  -->
                  <base-autocomplete
                    v-model="effect.content_id"
                    :items="cosmoformTemplates"
                    outlined
                    item-text="title"
                    item-value="id"
                    :disabled="isSubmiting"
                    :error-messages="effectContentError"
                    :has-error="!!effectContentError"
                    :loading="fetchingCosmoformTemplate"
                    clearable
                  />

                  <div class="input_label" v-text="$tc('utils.recipient', 0)" />
                  <v-radio-group v-model="formToContact">
                    <v-radio :label="$tc('utils.contact', 1)" :value="true" />
                    <v-radio :label="$t('utils.other_person')" :value="false" />
                  </v-radio-group>
                  <div v-if="!formToContact" class="advanced_content">
                    <div
                      class="input_label"
                      v-text="`${$t('utils.recipient_email')}*`"
                    />
                    <div class="input_section">
                      <base-text-field
                        v-model="effect.action_config"
                        :placeholder="$t('utils.recipient_email')"
                        :error-messages="actionConfigError"
                        :has-error="!!actionConfigError"
                        outlined
                      />
                    </div>
                  </div>
                </div>
                <!-- ACTION SEND_TO_GED  -->
                <div
                  v-else-if="effect.action === AUTOMATION_EFFECT.SEND_TO_GED"
                  class="action_section"
                >
                  <div class="template-content">
                    <div class="template-container">
                      <div class="input_label" v-text="$t('utils.template')" />
                      <base-text-area
                        v-model="effect.action_config"
                        outlined
                        :placeholder="
                          $t('automation.dialog.sendtoged_xml_template')
                        "
                        :error-messages="actionConfigError"
                        :has-error="!!actionConfigError"
                      />
                    </div>
                    <div class="template-container">
                      <div
                        class="input_label underline"
                        v-text="$t('utils.funtionning')"
                      />
                      <div>{{ templateGEd }}</div>
                      <div
                        class="input_label underline"
                        v-text="$t('utils.magical_variable')"
                      />
                      <div v-for="(variable, i) in magicalVariables" :key="i">
                        {{ variable }}
                      </div>
                    </div>
                  </div>
                </div>
                <!-- send campaing -->
                <div
                  v-else-if="effect.action === AUTOMATION_EFFECT.SEND_CAMPAIGN"
                  class="action_section"
                >
                  <div
                    class="input_label"
                    v-text="$t('automation.dialog.choose_campaign')"
                  />
                  <base-autocomplete
                    key="effect"
                    v-model="effect.content_id"
                    :items="effectCampaigns.items"
                    :loading="effectCampaignFilter.loading"
                    outlined
                    item-text="name"
                    item-value="id"
                    :disabled="isSubmiting"
                    clearable
                    :error-messages="effectContentError"
                    :has-error="!!effectContentError"
                    :filter="() => true"
                    :search-input.sync="effectCampaignFilter.name"
                  />
                </div>

                <!-- ACTION GENERATE_DOC -->
                <div
                  v-else-if="effect.action === AUTOMATION_EFFECT.GENERATE_DOC"
                  class="action_section"
                >
                  <div class="input_label" v-text="$t('utils.template')" />
                  <base-autocomplete
                    v-model="effect.content_id"
                    :items="documentTemplates"
                    outlined
                    item-text="name"
                    item-value="id"
                    :disabled="isSubmiting"
                    :error-messages="effectContentError"
                    :has-error="!!effectContentError"
                    :loading="fetchingDocumentTemplates"
                    clearable
                  />
                  <div
                    class="input_label"
                    v-text="`${$t('utils.destination')}`"
                  />
                  <base-autocomplete
                    v-model="effect.action_config"
                    :loading="questionsFilesFilter.loading"
                    :items="questionFiles.items"
                    :filter="() => true"
                    :search-input.sync="questionsFilesFilter.name"
                    item-text="name"
                    item-value="slug"
                    clearable
                    :error-messages="actionConfigError"
                    :has-error="!!actionConfigError"
                  >
                    <template #append-item>
                      <div v-intersect="loadMoreQuestionTemplates" />
                    </template>
                  </base-autocomplete>
                </div>
                <!-- ACTION ASSIGN_COLLABORATOR -->
                <div
                  v-else-if="
                    effect.action === AUTOMATION_EFFECT.ASSIGN_COLLABORATOR
                  "
                  class="action_section"
                >
                  <div class="input_label" v-text="$t('utils.destination')" />
                  <base-autocomplete
                    v-model="effect.collaborator"
                    :loading="collaboratorFilter.loading"
                    :items="collaboratorFilter.items"
                    :filter="() => true"
                    :search-input.sync="collaboratorFilter.email"
                    item-text="user.email"
                    :item-value="!automationActionToEdit ? '' : 'user.id'"
                    solo
                    :placeholder="`${$tc('organization.collaborator', 2)}`"
                    :error-messages="effectCollaboratorError"
                    :multiple="!automationActionToEdit"
                    :return-object="!automationActionToEdit"
                    :has-error="!!effectCollaboratorError"
                  >
                    <template #append-item>
                      <div v-intersect="loadMoreCollaborators" />
                    </template>
                  </base-autocomplete>
                  <div
                    v-if="
                      effect.collaborator &&
                      effect.collaborator.length &&
                      !automationActionToEdit
                    "
                    class="collaborator_section"
                  >
                    <div class="header">
                      <div class="item" />
                      <div class="item">Nom</div>
                      <div class="item">Declenchement unique</div>
                      <div class="item" />
                    </div>
                    <div
                      v-for="(collaborator, index) in effect.collaborator"
                      :key="collaborator.id"
                      class="collaborator_content"
                    >
                      <div class="item">{{ collaborator.user.email }}</div>
                      <div class="item">
                        <base-text-field
                          v-model="collaborator.label"
                          :placeholder="$t('automation.dialog.label_action')"
                          outlined
                        />
                      </div>
                      <div class="item">
                        <input
                          id="unique"
                          v-model="collaborator.unique"
                          type="checkbox"
                          name="unique"
                          class="ml-2"
                        />
                      </div>
                      <div class="item">
                        <BaseButton
                          color="secondary"
                          class="ml-2"
                          @click="() => effect.collaborator.splice(index, 1)"
                        >
                          {{ $tc("utils.delete") }}
                        </BaseButton>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <!-- LABEL SECTION -->
          <div
            v-if="
              effect.action !== AUTOMATION_EFFECT.ASSIGN_COLLABORATOR ||
              automationActionToEdit
            "
            class="advanced_content"
          >
            <div class="input_label" v-text="$t('utils.label')" />
            <div class="input_section">
              <base-text-field
                v-model="effect.label"
                :placeholder="$t('automation.dialog.label_action')"
                :errors="!!effectLabelError"
                :error-messages="effectLabelError"
                outlined
              />
            </div>
          </div>
          <!-- ADVANCED SETTING -->
          <div class="advanced_content">
            <!-- LOCATION -->
            <div class="advanced_input">
              <div class="input_label" v-text="$tc('utils.status', 2)" />

              <div class="input_section">
                <base-autocomplete
                  :items="[
                    { name: $t('utils.selectAll'), id: -1 },
                    ...workflowStatuses,
                  ]"
                  :placeholder="$t('automation.dialog.choose_status')"
                  outlined
                  :value="statuses"
                  item-text="name"
                  item-value="id"
                  :disabled="isSubmiting"
                  multiple
                  clearable
                  display-selection
                  @change="setAllChecked"
                >
                  <template #selection="{ item, index }">
                    <div v-if="item.id !== -1">
                      {{
                        `${item.name}${
                          workflowStatuses.length >
                          (automationActionToEdit ? index : index + 1)
                            ? ","
                            : ""
                        }`
                      }}
                    </div>
                  </template>
                </base-autocomplete>
              </div>
            </div>
            <!-- SINGLE TRIGGER -->
            <div
              v-if="
                effect.action !== AUTOMATION_EFFECT.ASSIGN_COLLABORATOR ||
                automationActionToEdit
              "
              class="checkbox_item"
            >
              <label for="unique" v-text="$t('utils.single_trigger')" />
              <input
                id="unique"
                v-model="unique"
                type="checkbox"
                name="unique"
                class="ml-2"
              />
            </div>
          </div>
        </div>
        <div class="modal_footer">
          <BaseButton
            :disabled="isSubmiting"
            color="grey"
            type="secondary"
            @click="reset()"
          >
            {{ $t("utils.cancel") }}
          </BaseButton>
          <BaseButton
            :disabled="isSubmiting"
            color="primary"
            type="primary"
            @click="submit()"
          >
            {{ $t("utils.validate") }}
          </BaseButton>
        </div>
      </BaseCosmoCard>
    </template>
  </BaseDialog>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import WorkflowAPI from "@/modules/Admin/Services/workflow.api";
import {
  getOrganizationCosmoformTemplate,
  getOrganizationWorkflowCosmoformTemplate,
  getWorkflowQuestions,
} from "@/modules/Admin/Services/workflow.service";
import { getOrganizationCampaigns } from "@/modules/Editor/Services/campaign.service";
import { AUTOMATION_EFFECT, AUTOMATION_EVENT } from "@/core/Utils/types.utils";
import OrganizationAPI from "@/core/Services/organization.api";

export default {
  name: "EffectCreateAndEditDialog",
  mixins: [validationMixin],
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    hasEnoxa: {
      type: Boolean,
    },
    workflow: {
      type: Object,
      required: true,
    },
    selectedEffect: {
      type: String,
      required,
      default: AUTOMATION_EFFECT.SET_STATUS,
    },
    automationActionToEdit: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      formToContact: true,
      openAdvancedSettings: false,
      isFetchingCampaign: true,
      isSubmiting: false,
      collaboratorPayload: [],
      checkAllStatus: true,
      questionsFilesFilter: {
        loading: false,
        page: 1,
        slug: undefined,
        pageSize: 10,
        name: undefined,
        searchTimeoutRef: undefined,
        typeQuestion: ["FILE", "MULTIPLE_FILE"],
      },
      questionFiles: {
        items: [],
        count: 0,
        next: 0,
        previous: 0,
      },
      collaboratorFilter: {
        loading: false,
        page: 1,
        items: [],
        pageSize: 10,
        email: undefined,
        searchTimeoutRef: undefined,
      },
      effectItems: [
        {
          label: this.$t("automation.effect.set_status"),
          value: AUTOMATION_EFFECT.SET_STATUS,
        },
        {
          label: this.$t("automation.effect.send_offer"),
          value: AUTOMATION_EFFECT.SEND_OFFER,
        },
        {
          label: this.$t("automation.effect.send_form"),
          value: AUTOMATION_EFFECT.SEND_FORM,
        },
        {
          label: this.$t("automation.effect.send_to_ged"),
          value: AUTOMATION_EFFECT.SEND_TO_GED,
          // disabled: !this.hasEnoxa,
        },
        {
          label: this.$t("automation.effect.archive_folder"),
          value: AUTOMATION_EFFECT.ARCHIVE_FOLDER,
        },
        {
          label: this.$t("automation.effect.send_campaign"),
          value: AUTOMATION_EFFECT.SEND_CAMPAIGN,
        },
        {
          label: this.$t("automation.effect.generate_doc"),
          value: AUTOMATION_EFFECT.GENERATE_DOC,
        },
        {
          label: this.$t("automation.effect.assign_collaborator"),
          value: AUTOMATION_EFFECT.ASSIGN_COLLABORATOR,
        },
      ],
      eventItems: [
        {
          label: this.$t("automation.event_config.manual"),
          value: AUTOMATION_EVENT.MANUAL,
        },
      ],
      effect: {
        label: "",
        action: null,
        content_id: null,
        action_config: null,
        collaborator: [],
      },
      eventEffect: {
        action: null,
      },
      unique: false,
      offerTemplates: {
        items: [],
        count: 0,
        next: 0,
        previous: 0,
      },
      // effect
      effectCampaignFilter: {
        loading: false,
        page: 1,
        pageSize: 200,
        name: undefined,
        status: "IN_PROGRESS",
        searchTimeoutRef: undefined,
      },
      effectCampaigns: {
        items: [],
        count: 0,
        next: 0,
        previous: 0,
      },
      statuses: [],
      fetchingOfferTemplate: false,
      fetchingCosmoformTemplate: false,
      cosmoformTemplates: [],
      fetchingDocumentTemplates: false,
      documentTemplates: [],
      AUTOMATION_EFFECT: AUTOMATION_EFFECT,
      AUTOMATION_EVENT: AUTOMATION_EVENT,
      validationSchema: {
        loading: false,
        items: [],
      },
      isInitializing: true,
    };
  },
  validations() {
    let validations = {};
    validations.effect = {
      action: {
        required,
      },
      label: {
        required: requiredIf(
          () => this.effect.action === !AUTOMATION_EFFECT.ASSIGN_COLLABORATOR
        ),
      },
      collaborator: {
        required: requiredIf(
          () => this.effect.action === AUTOMATION_EFFECT.ASSIGN_COLLABORATOR
        ),
      },
      content_id: {
        required: requiredIf(
          () =>
            ![
              AUTOMATION_EFFECT.ARCHIVE_FOLDER,
              AUTOMATION_EFFECT.SEND_TO_GED,
              AUTOMATION_EFFECT.ASSIGN_COLLABORATOR,
            ].includes(this.effect.action)
        ),
      },
      action_config: {
        required: requiredIf(
          () =>
            [
              AUTOMATION_EFFECT.GENERATE_DOC,
              AUTOMATION_EFFECT.SEND_TO_GED,
            ].includes(this.effect.action) ||
            (this.effect.action == AUTOMATION_EFFECT.SEND_FORM &&
              !this.formToContact)
        ),
      },
    };
    return validations;
  },
  computed: {
    wfStatusItems() {
      // Hide the “Select All” item if it’s already selected
      const hasSelectAll = this.statuses.includes(-1);

      const baseItems = [
        { name: this.$t("utils.selectAll"), id: -1 },
        ...this.workflowStatuses,
      ];

      return hasSelectAll
        ? baseItems.filter((item) => item.id !== -1)
        : baseItems;
    },
    templateGEd() {
      return `Le template permet d'indiquer a la GED Enoxa comment
                        ranger et trier les documents du dossiers envoyés. Il
                        doit être crée a partir des règles de rangement de votre
                        GED. Il est possible d'utiliser les données du dossier
                        en utilisant la syntaxe suivante: {{ XXX }} ou XXX est
                        le slug de la donnee du dossier`;
    },
    defaultToGed() {
      return `<root>
  <organization>
    <name>{{__O_NAME__}}</name>
  </organization>
  <file>
  <type>{{__Q_SLUG__}}</type>
  </file>
  <folder>
  <name>{{__F_NAME__}}</name>
  </folder>
  <data>
  <ref>{{STD_REF}}</ref>
</data>
{{__METADATA__}}
</root>`;
    },
    magicalVariables() {
      return [
        "{{__Q_SLUG__}} Slug de la question.",
        "{{__Q_LABEL__}} Label de la question.",
        "{{__F_NAME__}} Nom du dossier.",
        "{{__F_EXT__}} Extension du fichier.",
        "{{__O_NAME__}} Nom de l'organisation ",
        "{{__METADATA__}} Données extraites du document. ",
      ];
    },
    workflowStatuses() {
      if (!this.workflow?.statuses) return [];
      return this.workflow.statuses;
    },
    effectActionError() {
      if (!this.$v.effect.action.$dirty) return "";
      if (!this.$v.effect.action.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
    effectLabelError() {
      if (!this.$v.effect.label.$dirty) return "";
      if (!this.$v.effect.label.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
    effectContentError() {
      if (!this.$v.effect.content_id.$dirty) return "";
      if (!this.$v.effect.content_id.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
    effectCollaboratorError() {
      if (!this.$v.effect.collaborator.$dirty) return "";
      if (!this.$v.effect.collaborator.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
    actionConfigError() {
      if (!this.$v.effect.action_config.$dirty) return "";
      if (!this.$v.effect.action_config.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
    dialogTitle() {
      return this.effectItems.find(
        (effect) => effect.value === this.selectedEffect
      )?.label;
    },
  },
  watch: {
    "effectCampaignFilter.name"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterEffectCampaigns();
    },
    "questionsFilesFilter.name"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.questionsFilesFilter.slug = "";
      this.filterQuestionFileMultipleFiles();
    },
    "collaboratorFilter.email"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterCollaborators();
    },
    automationActionToEdit: {
      handler() {
        if (!this.automationActionToEdit) return (this.isInitializing = false);
        this.buildEventAndActionValue();
      },
      immediate: true,
    },
    selectedEffect: {
      handler() {
        if (!this.selectedEffect) return;
        this.effect.action = this.selectedEffect;
      },
      immediate: true,
    },
  },
  created() {
    this.handleEffectChange();
    if (!this.automationActionToEdit) this.checkStatus();
  },
  methods: {
    setAllChecked(item) {
      const oldStatuses = [...this.statuses];
      const oldStatusesHasSelectAll = oldStatuses.some((stat) => stat === -1);
      const newItemHasSelectAll = item.some((stat) => stat === -1);
      if (!oldStatusesHasSelectAll && newItemHasSelectAll) {
        return (this.statuses = [
          ...this.workflowStatuses.map((wfStat) => wfStat.id),
          -1,
        ]);
      }
      if (oldStatusesHasSelectAll && !newItemHasSelectAll) {
        return (this.statuses = []);
      }

      if (oldStatusesHasSelectAll && newItemHasSelectAll) {
        this.statuses = [...item];
        if (item.length !== this.workflowStatuses.length + 1) {
          this.statuses = [...this.statuses.filter((stat) => stat !== -1)];
        }
        return;
      }
      if (
        !oldStatusesHasSelectAll &&
        !newItemHasSelectAll &&
        item.length === this.workflowStatuses.length
      ) {
        this.statuses = [...item, -1];
      } else {
        this.statuses = [...item];
      }
    },
    checkStatus() {
      return (this.statuses = [
        ...this.workflowStatuses.map((wfStat) => wfStat.id),
        -1,
      ]);
    },
    reset() {
      if (this.isSubmiting) return;
      this.$emit("close");
    },
    async fetchCollaborators() {
      if (
        this.collaboratorFilter.loading &&
        !this.collaboratorFilter.page &&
        !this.organizationId
      )
        return;
      this.collaboratorFilter.loading = true;
      const res = await OrganizationAPI.getOrganizationCollaborators(
        this.organizationId,
        this.collaboratorFilter.page,
        this.collaboratorFilter.email
      );
      this.collaboratorFilter = {
        ...this.collaboratorFilter,
        items: [...res.data.results],
        next: res.data.next,
      };
      this.collaboratorFilter.loading = false;
    },
    filterCollaborators() {
      this.collaboratorFilter.page = 1;
      if (this.collaboratorFilter.searchTimeoutRef)
        clearTimeout(this.collaboratorFilter.searchTimeoutRef);
      this.collaboratorFilter.searchTimeoutRef = setTimeout(async () => {
        this.fetchCollaborators();
      }, 200);
    },
    async loadMoreCollaborators(_, __, isIntersecting) {
      if (
        !isIntersecting ||
        this.collaboratorFilter.loading ||
        !this.collaboratorFilter.next
      )
        return;
      this.collaboratorFilter.page = this.collaboratorFilter.next;
      this.collaboratorFilter.loading = true;
      try {
        const res = await OrganizationAPI.getOrganizationCollaborators(
          this.organizationId,
          this.collaboratorFilter.page,
          this.collaboratorFilter.email
        );
        this.collaboratorFilter = {
          items: [...this.collaboratorFilter.items, ...res.data.results],
          next: res.data.next,
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.questionsFilesFilter.loading = false;
    },
    async buildEventAndActionValue() {
      this.isInitializing = true;
      // init value
      this.effect = {
        action: this.automationActionToEdit.action,
        label: this.automationActionToEdit.label,
      };
      this.unique = this.automationActionToEdit.unique;
      this.statuses = this.automationActionToEdit.statuses.map(
        (action) => action.id
      );

      if (this.statuses.length === this.workflowStatuses.length) {
        this.statuses.unshift(-1);
      }

      // fetching corresponding value
      await this.handleEffectChange();
      // building content_id
      if (this.effect.action == AUTOMATION_EFFECT.ASSIGN_COLLABORATOR) {
        this.effect.collaborator = this.getContentId(
          this.effect.action,
          this.automationActionToEdit.actionExtra
        );
      } else
        this.effect.content_id = this.getContentId(
          this.effect.action,
          this.automationActionToEdit.actionExtra
        );
      this.effect.action_config = this.getActionConfigId(
        this.effect.action,
        this.automationActionToEdit.actionConfig
      );
      if (
        this.automationActionToEdit.action == AUTOMATION_EFFECT.SEND_FORM &&
        this.automationActionToEdit.actionConfig?.email
      ) {
        this.formToContact = false;
        this.effect.action_config =
          this.automationActionToEdit.actionConfig.email;
      }
      this.isInitializing = false;
    },
    getContentId(type, info) {
      switch (type) {
        case "ASSIGN_COLLABORATOR": {
          return this.collaboratorFilter.items.find(
            (collaborator) => collaborator.user.email == info.email
          )?.id;
        }
        case AUTOMATION_EVENT.STATUS_IS_SET:
        case AUTOMATION_EFFECT.SET_STATUS:
          return [...this.workflowStatuses].find(
            (status) => status.name === info.name
          )?.id;
        case AUTOMATION_EFFECT.SEND_OFFER:
          return [...this.offerTemplates.items].find(
            (offerTemplate) => offerTemplate.title === info.title
          )?.id;
        case AUTOMATION_EFFECT.SEND_FORM:
          return [...this.cosmoformTemplates].find(
            (cosmoformTemplate) => cosmoformTemplate.title === info.title
          )?.id;
        case AUTOMATION_EFFECT.GENERATE_DOC:
          return [...this.documentTemplates].find(
            (documentTemplate) => documentTemplate.name === info.name
          )?.id;
        case AUTOMATION_EVENT.WIZIDEE_CONTROL_FAILED:
          return [...this.validationSchema.items].find(
            (validation) => validation.name === info.name
          )?.id;
        case AUTOMATION_EFFECT.SEND_CAMPAIGN:
        case AUTOMATION_EVENT.CAMPAIGN_ANSWERED: {
          const value = this.effectCampaigns.items.length
            ? [...this.effectCampaigns.items]
            : [...this.eventCampaigns.items];
          return value.find((campaign) => campaign.name === info.name)?.id;
        }

        default:
          return null;
      }
    },
    getActionConfigId(type, config) {
      switch (type) {
        case AUTOMATION_EFFECT.SEND_TO_GED:
          return config.xmlPattern;
        case AUTOMATION_EFFECT.GENERATE_DOC:
          return [...this.questionFiles.items].find(
            (question) => question.slug === config.destination
          )?.slug;
        default:
          return null;
      }
    },
    async fetchQuestionFileMultipleFile() {
      this.questionsFilesFilter.loading = true;
      try {
        this.questionsFilesFilter.page = 1;
        this.questionFiles = await getWorkflowQuestions({
          organizationId: this.$route.params.organizationId,
          workflowId: this.$route.params.workflowId,
          page: this.questionsFilesFilter.page,
          name: this.questionsFilesFilter.name,
          slug: this.questionsFilesFilter.slug,
          pageSize: this.questionsFilesFilter.pageSize,
          typeQuestion: this.questionsFilesFilter.typeQuestion,
        });
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.questionsFilesFilter.loading = false;
    },
    filterQuestionFileMultipleFiles() {
      if (this.questionsFilesFilter.searchTimeoutRef)
        clearTimeout(this.questionsFilesFilter.searchTimeoutRef);
      this.questionsFilesFilter.searchTimeoutRef = setTimeout(async () => {
        this.fetchQuestionFileMultipleFile();
      }, 200);
    },
    async loadMoreQuestionTemplates(_, __, isIntersecting) {
      if (
        !isIntersecting ||
        this.questionsFilesFilter.loading ||
        !this.questionFiles.next
      )
        return;
      this.questionsFilesFilter.page = this.questionFiles.next;
      this.questionsFilesFilter.loading = true;
      try {
        const tmp = await getWorkflowQuestions({
          organizationId: this.$route.params.organizationId,
          workflowId: this.$route.params.workflowId,
          page: this.questionsFilesFilter.page,
          slug: this.questionsFilesFilter.slug,
          pageSize: this.questionsFilesFilter.pageSize,
        });
        this.questionFiles = {
          ...tmp,
          items: [...this.questionFiles.items, ...tmp.items],
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.questionsFilesFilter.loading = false;
    },

    async fecthOfferTemplates() {
      this.fetchingOfferTemplate = true;
      const response = await OrganizationAPI.getOrganizationOfferTemplate({
        organizationId: this.organizationId,
        filters: {
          page: 1,
          offer_type: ["CONSULT", "VALIDATION"],
        },
      });
      this.offerTemplates = {
        ...response.data,
        items: response.data.results,
      };
      this.fetchingOfferTemplate = false;
    },
    async fetchCosmoformTemplates() {
      this.fetchingCosmoformTemplate = true;
      const promises = await Promise.all([
        getOrganizationWorkflowCosmoformTemplate({
          organizationId: this.$route.params.organizationId,
          workflowId: this.$route.params.workflowId,
        }),
        await getOrganizationCosmoformTemplate({
          organizationId: this.$route.params.organizationId,
        }),
      ]);
      this.cosmoformTemplates = [...promises[0].item, ...promises[1].item];
      this.fetchingCosmoformTemplate = false;
    },
    // campaign effect
    async fetchEffectCampaigns() {
      this.effectCampaignFilter.loading = true;
      try {
        this.effectCampaignFilter.page = 1;
        const res = await getOrganizationCampaigns({
          organizationId: this.organizationId,
          filters: this.effectCampaignFilter,
        });
        this.effectCampaigns = {
          ...res.data,
          items: [...res.data.results],
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.effectCampaignFilter.loading = false;
    },
    filterEffectCampaigns() {
      if (this.effectCampaignFilter.searchTimeoutRef)
        clearTimeout(this.effectCampaignFilter.searchTimeoutRef);
      this.effectCampaignFilter.searchTimeoutRef = setTimeout(async () => {
        this.fetchEffectCampaigns();
      }, 500);
    },
    async fetchDocumentTemplates() {
      this.fetchingDocumentTemplates = true;
      try {
        const res = await OrganizationAPI.getOrganizationDocumentTemplates(
          this.organizationId
        );
        this.documentTemplates = res.data.results;
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }

      this.fetchingDocumentTemplates = false;
    },
    async fetchValidationSchema() {
      this.validationSchema.loading = true;
      try {
        const res = await OrganizationAPI.getOrganizationValidationSchema(
          this.organizationId
        );
        this.validationSchema.items = res.data.results;
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.validationSchema.loading = false;
    },
    getPayload(collaboratorContentID, unique, label) {
      const payload = {};

      // action
      payload.action = this.effect.action;

      // label
      payload.label = label || this.effect.label;

      // action_content_id
      if (collaboratorContentID) {
        payload.action_content_id = collaboratorContentID;
      } else if (this.effect.content_id || this.effect.collaborator?.length) {
        payload.action_content_id =
          this.effect.content_id || this.effect.collaborator;
      }

      // action_config
      if (this.effect.action_config) {
        switch (this.effect.action) {
          case AUTOMATION_EFFECT.SEND_TO_GED:
            payload.action_config = { xml_pattern: this.effect.action_config };
            break;
          case AUTOMATION_EFFECT.GENERATE_DOC:
            payload.action_config = { destination: this.effect.action_config };
            break;
          case AUTOMATION_EFFECT.SEND_FORM:
            payload.action_config = { email: this.effect.action_config };
            break;
          default:
            payload.action_config = this.effect.action_config;
        }
      }

      // unique
      payload.unique = unique !== undefined ? unique : this.unique ?? false;

      // statuses
      const statFiltered = this.statuses.filter((stat) => stat !== -1);
      if (statFiltered && statFiltered.length) {
        payload.statuses = [...statFiltered];
      } else {
        payload.statuses = [];
      }

      // event
      if (!this.automationActionToEdit) {
        payload.event = AUTOMATION_EVENT.MANUAL;
      }

      return payload;
    },

    async postPatchEffect(payload) {
      if (this.automationActionToEdit) {
        await WorkflowAPI.patchOrganizationWorkflowEffet({
          organizationId: this.organizationId,
          workflowId: this.workflow.id,
          effectId: this.automationActionToEdit.id,
          payload: payload,
        });
      } else {
        await WorkflowAPI.postOrganizationWorkflowEffet({
          organizationId: this.organizationId,
          workflowId: this.workflow.id,
          payload: payload,
        });
      }
    },
    async submit() {
      this.$v.effect.$touch();
      if (this.$v.effect.$invalid) return;
      this.isSubmiting = true;
      try {
        if (
          this.effect.action === AUTOMATION_EFFECT.ASSIGN_COLLABORATOR &&
          !this.automationActionToEdit
        ) {
          const effectPromises = this.effect.collaborator.map(
            async (collaborator) => {
              const unique = !!collaborator.unique;
              return this.postPatchEffect(
                this.getPayload(
                  collaborator.user.id,
                  unique,
                  collaborator.label
                )
              );
            }
          );
          await Promise.all(effectPromises);
        } else await this.postPatchEffect(this.getPayload());

        this.$store.dispatch("snackbar/active", {
          message: this.$t(
            this.automationActionToEdit
              ? "automation.effect.modify_sucessfully"
              : "automation.effect.created_sucessfully"
          ),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("workflow.snackbar.error.errorModifyingWorkflow"),
          type: "ERROR",
        });
      }
      this.isSubmiting = false;
      this.$emit("effect-created");
      this.reset();
    },
    async handleEffectChange() {
      this.effect.content_id = null;
      try {
        switch (this.effect.action) {
          case AUTOMATION_EFFECT.SEND_OFFER:
            if (!this.offerTemplates.items.length)
              await this.fecthOfferTemplates();
            break;
          case AUTOMATION_EFFECT.SEND_TO_GED:
            this.effect.action_config = this.defaultToGed;
            break;
          case AUTOMATION_EFFECT.SEND_FORM:
            if (!this.cosmoformTemplates.length)
              await this.fetchCosmoformTemplates();
            break;
          case AUTOMATION_EFFECT.SEND_CAMPAIGN:
            if (!this.effectCampaigns.items.length)
              await this.fetchEffectCampaigns();
            break;
          case AUTOMATION_EFFECT.ASSIGN_COLLABORATOR:
            if (!this.collaboratorFilter.items.length)
              await this.fetchCollaborators();
            break;
          case AUTOMATION_EFFECT.GENERATE_DOC: {
            if (this.automationActionToEdit)
              this.questionsFilesFilter.slug =
                this.automationActionToEdit.actionConfig.destination;
            if (!this.documentTemplates.length) {
              await this.fetchDocumentTemplates();
              await this.fetchQuestionFileMultipleFile();
            }
            break;
          }
        }
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: 8px;
  flex-wrap: wrap;
  .modal_title {
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #353542;
  }
  .close_icon {
    margin-left: auto;
  }
}
.modal_content {
  padding-top: 24px;
  position: relative;

  .input_group {
    display: flex;
    gap: 16px;
    .group_label {
      flex: none;
      width: 80px;
      height: 42px;
      display: flex;
      align-items: center;
      font-family: Inter;
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      color: #353542;
    }
    .input_section {
      width: 100%;
      flex: none;
      display: flex;
      gap: 16px;
      .action_section {
        display: flex;
        flex-direction: column;
        .collaborator_section {
          display: flex;
          flex-direction: column;
          gap: 12px;
          .header {
            width: 100%;
            display: flex;
            .item {
              flex: 1;
              text-decoration: underline;
            }
          }
          .collaborator_content {
            width: 100%;
            display: flex;
            .item {
              flex: 1;
              text-align: center;
            }
          }
        }
        .template-content {
          display: flex;
          gap: 16px;
          .template-container {
            flex: 1;
            .underline {
              text-decoration: underline;
            }
          }
        }
      }
      & > * {
        flex: 1;
      }
      .inputs {
        display: flex;
        gap: 16px;
        & > * {
          width: calc(50% - 8px);
          flex: none;
        }
      }
    }
  }
  .advanced_section {
    .advanced_header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      cursor: pointer;
      .chevron_btn {
        width: 24px;
        flex: none;
        transition: all 0.3s ease-out;
        &.expanded {
          transform: rotate(90deg);
        }
      }
      .header_label {
        flex: none;
        width: 160px;
        align-items: center;
        font-family: Inter;
        font-size: 16px;
        font-weight: 600;
        line-height: 20px;
        color: #353542;
      }
    }
    .advanced_content {
      .advanced_input {
        display: flex;
        .input_label {
          width: 176px;
          height: 42px;
          display: flex;
          align-items: center;
          color: #353542;
          font-family: Inter;
          font-size: 14px;
          line-height: 18px;
          font-style: normal;
          font-weight: 400;
          &.underline {
            text-decoration: underline;
          }
        }
        .input_section {
          width: calc(100% - 176px);
          flex: none;
        }
      }
      .checkbox_item {
        margin-bottom: 16px;
        display: flex;
        align-items: center;
        label {
          width: 176px;
          color: #353542;
          font-family: Inter;
          font-size: 14px;
          line-height: 18px;
          font-style: normal;
          font-weight: 400;
        }
        input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
      }
    }
  }
  .progression {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 10;
  }
}
.modal_footer {
  display: flex;
  justify-content: flex-end;
  align-content: center;
  padding: 8px 0;
  gap: 16px;
}
.mail_chips {
  background: rgba(155, 189, 255, 0.2) !important;
  color: #2536cc !important;
  border-radius: 21px;
  ::v-deep .v-chip__close {
    color: #2536cc;
  }
}
.notification_skeleton {
  height: 40px;
  width: 100%;
  ::v-deep {
    .v-skeleton-loader__bone {
      height: 40px;
    }
  }
}
::v-deep textarea {
  min-height: 400px;
}
</style>
